import { useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { formatRands } from "@/lib/order";
import { useCart } from "@/context/cart-context";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/common/States";

export default function Cart() {
  const cart = useCart();
  const navigate = useNavigate();

  if (cart.count === 0) {
    return (
      <EmptyState
        icon={ShoppingBag}
        title="Your cart is empty"
        description="Browse local stores and add something delicious."
        action={<Button className="rounded-full" onClick={() => navigate("/")} data-testid="browse-stores-button">Browse stores</Button>}
        testId="empty-cart"
      />
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-3">
        <h1 className="font-display text-2xl">Your cart</h1>
        <p className="text-sm text-muted-foreground">From {cart.vendorName}</p>
        {cart.list.map((it) => (
          <Card key={it.product_id} className="flex items-center gap-4 rounded-2xl p-3" data-testid={`cart-item-${it.product_id}`}>
            {it.image_url && <img src={fileUrl(it.image_url)} alt={it.name} className="h-16 w-16 rounded-xl object-cover" />}
            <div className="min-w-0 flex-1">
              <p className="font-display text-base leading-tight">{it.name}</p>
              <p className="text-sm text-muted-foreground">{formatRands(it.unit_price)}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => cart.setQty(it.product_id, it.quantity - 1)}>
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-5 text-center text-sm">{it.quantity}</span>
              <Button size="icon" className="h-8 w-8 rounded-full" onClick={() => cart.setQty(it.product_id, it.quantity + 1)}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => cart.setQty(it.product_id, 0)} data-testid={`remove-${it.product_id}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </Card>
        ))}
      </div>

      <div>
        <Card className="sticky top-24 rounded-2xl p-5">
          <h2 className="font-display text-lg">Summary</h2>
          <div className="mt-4 flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span>{formatRands(cart.subtotal)}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Delivery & fees calculated at checkout.</p>
          <Button className="mt-5 w-full rounded-full" onClick={() => navigate("/checkout")} data-testid="checkout-button">
            Proceed to checkout
          </Button>
        </Card>
      </div>
    </div>
  );
}
